<template>
  <WdsStack direction-column class="tw-w-full" data-test="compliance-review-decision">
    <WdsAlert v-if="errorMessage" error class="tw-mb-3">{{ errorMessage }}</WdsAlert>

    <div
      class="tw-grid tw-transition-[grid-template-rows,opacity] tw-duration-300 tw-ease-out motion-reduce:tw-transition-none"
      :class="requestingChanges ? 'tw-opacity-100' : 'tw-opacity-0'"
      :style="{ gridTemplateRows: requestingChanges ? '1fr' : '0fr' }"
      :aria-hidden="!requestingChanges"
      :inert="!requestingChanges || undefined"
      data-test="compliance-review-decision-panel"
    >
      <div class="tw-min-h-0 tw-overflow-hidden">
        <WdsStack
          v-if="changesMounted"
          direction-column
          gap-sm
          class="tw-pb-4"
          data-test="compliance-review-changes-panel"
        >
          <WdsStack direction-column gap-xs>
            <WdsText class="tw-font-semibold">
              {{ $t('Request changes from :name', { name: displayName }) }}
            </WdsText>
            <WdsText sm secondary>
              {{ $t(':name gets an email with your note and their status changes to Changes requested.', { name: firstName }) }}
            </WdsText>
          </WdsStack>
          <WdsInputArea
            v-model="note"
            :label="$t('Add note (sent to the worker) *')"
            height="72px"
            resizeable
            data-test="compliance-review-note"
          />
        </WdsStack>
      </div>
    </div>

    <WdsStack
      align-center
      justify-space-between
      gap-md
      class="tw-flex-wrap"
      data-test="compliance-review-decision-actions"
    >
      <div class="tw-min-w-0 tw-flex-1">
        <Transition
          enter-active-class="tw-transition-opacity tw-duration-200 motion-reduce:tw-transition-none"
          enter-from-class="tw-opacity-0"
          leave-active-class="tw-transition-opacity tw-duration-100 motion-reduce:tw-transition-none"
          leave-to-class="tw-opacity-0"
        >
          <WdsStack
            v-if="decision === 'CLEARED'"
            align-center
            gap-sm
            class="tw-flex-wrap"
            data-test="compliance-review-clear-confirm"
          >
            <WdsText class="tw-font-semibold">
              {{ $t('Mark :name as cleared?', { name: displayName }) }}
            </WdsText>
            <WdsLabel
              v-if="incompleteCount > 0"
              sm
              warning
              subtle
              :label="$tc(':count compliance is incomplete | :count compliances are incomplete', incompleteCount)"
              data-test="compliance-review-incomplete-warning"
            />
            <WdsText sm secondary>
              {{ $t('The worker will see an in-app notification. No email is sent.') }}
            </WdsText>
          </WdsStack>
        </Transition>
      </div>

      <Transition
        mode="out-in"
        enter-active-class="tw-transition-opacity tw-duration-150 motion-reduce:tw-transition-none"
        enter-from-class="tw-opacity-0"
        leave-active-class="tw-transition-opacity tw-duration-100 motion-reduce:tw-transition-none"
        leave-to-class="tw-opacity-0"
      >
        <WdsStack v-if="decision === ''" key="choices" direction-row justify-end gap-sm class="tw-flex-wrap">
          <WdsButton
            :disabled="loading"
            data-test="compliance-review-changes-requested"
            @click="choose('CHANGES_REQUESTED')"
          >
            {{ $t('Request changes') }}
          </WdsButton>
          <WdsButton
            primary
            :disabled="loading"
            data-test="compliance-review-cleared"
            @click="choose('CLEARED')"
          >
            {{ $t('Mark as cleared') }}
          </WdsButton>
        </WdsStack>

        <WdsStack v-else key="confirm" direction-row justify-end gap-sm class="tw-flex-wrap">
          <WdsButton tertiary :disabled="submitting" data-test="compliance-review-decision-cancel" @click="reset">
            {{ $t('Cancel') }}
          </WdsButton>
          <WdsButton
            :primary="requestingChanges"
            :success="decision === 'CLEARED'"
            :disabled="!canSubmit || submitting"
            data-test="compliance-review-save"
            @click="handleSubmit"
          >
            <WdsSpinner v-if="submitting" :size="14" indeterminate />
            <template v-else>{{ decision === 'CLEARED' ? $t('Confirm') : $t('Send request') }}</template>
          </WdsButton>
        </WdsStack>
      </Transition>
    </WdsStack>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsInputArea,
  WdsLabel,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import type { ReviewDecision } from '@/components/compliances/ComplianceReviewModal/ComplianceReviewDecisionForm.vue'

const props = withDefaults(
  defineProps<{
    workerName?: string
    incompleteCount?: number
    loading?: boolean
    submitting: boolean
    errorMessage: string
  }>(),
  { workerName: undefined, incompleteCount: 0 },
)

const emit = defineEmits<{
  submit: [{ decision: ReviewDecision; note: string | null }]
}>()

const decision = ref<ReviewDecision | ''>('')
const note = ref('')

const requestingChanges = computed(() => decision.value === 'CHANGES_REQUESTED')

// The note panel keeps its content while it folds away, so closing it doesn't blank it first.
const changesMounted = ref(false)

watch(requestingChanges, (value) => {
  if (value) {
    changesMounted.value = true
  }
})

const displayName = computed(() => props.workerName || $t('the worker'))
const firstName = computed(() => props.workerName?.split(' ')[0] || $t('The worker'))

// Changes go to the worker as a message, so a note is what makes the request.
const canSubmit = computed(
  () => decision.value === 'CLEARED' || (decision.value === 'CHANGES_REQUESTED' && note.value.trim() !== ''),
)

function choose(value: ReviewDecision) {
  if (props.loading) {
    return
  }

  note.value = ''
  decision.value = value
}

function reset() {
  decision.value = ''
}

function handleSubmit() {
  if (decision.value === '' || !canSubmit.value || props.submitting) {
    return
  }

  emit('submit', {
    decision: decision.value,
    note: decision.value === 'CHANGES_REQUESTED' ? note.value.trim() : null,
  })
}
</script>
